import * as React from 'react';

import {FetchMoreButton} from 'shells/table/fetch-more-button';
import {LegacySetupTableEmptyState} from 'shells/table/legacy-setup-table-empty-state';
import {LegacySetupTableWrapper} from 'shells/table/legacy-setup-table-wrapper';
import {SetupItemTable} from 'shells/table/setup-item-table';
import type {Row} from 'shells/table/types';

import type {ChannelType} from 'nutshell-graphql-types';

import {useGetAnalyticsStatus} from '../../../email-marketing/analytics/use-get-analytics-status';

import {AnalyticsInfoBanner} from '../../analytics/analytics-info-banner';

import type {SourceValues} from '../types';
import {useGetSources} from '../use-get-sources';

import {getDisplayableType, getReportUrl} from './helpers';
import {SourceRowActionMenu} from './source-row-action-menu';

type Props = {
    onAddClick: () => void;
    onEditClick: (input: SourceValues) => void;
    onDelete: (arg1: {id: string; channel: ChannelType | null | undefined}) => void;
};

export function SetupNoChannelTable(props: Props) {
    const {sources, totalCount, isLoading, error, hasNextPage, fetchMore} = useGetSources({
        channel: null,
    });

    const analytics = useGetAnalyticsStatus();

    const getBannerForUser = () => {
        if (
            analytics &&
            analytics.analyticsStatus &&
            analytics.analyticsStatus.status !== 'CONFIGURED'
        ) {
            return (
                <div className='mb-16'>
                    <AnalyticsInfoBanner />
                </div>
            );
        }
    };

    const handleDeleteSource = (sourceId: string) => {
        props.onDelete({
            id: sourceId,
            channel: null,
        });
    };

    const renderRowActionMenu = (row: Row<any>) => (
        <SourceRowActionMenu
            name={row.original.name}
            // @ts-expect-error
            onDeleteSource={
                row.original.isDeleteable ? () => handleDeleteSource(row.original.id) : undefined
            }
            reportUrl={getReportUrl(row.original.id)}
        />
    );

    return (
        <>
            {getBannerForUser()}
            <LegacySetupTableWrapper
                title='No channel'
                count={totalCount}
                helpInfo='Sources in this table have not yet been added to a channel. Click each source to edit.'
            >
                <SetupItemTable
                    items={sources.map((source) => ({
                        id: source.id,
                        name: source.name,
                        type: getDisplayableType(source.type),
                        isDeleteable: source.isDeleteable,
                        isEditable: source.isEditable,
                        createdTime: source.createdTime,
                        reportUrl: getReportUrl(source.id),
                        lastUsedTime: source.lastUsedTime,
                    }))}
                    title='Source'
                    isLoading={isLoading}
                    error={error}
                    canLoadMore={hasNextPage}
                    onAddRowClick={props.onAddClick}
                    onSelectRow={(row: Row<any>) => {
                        const id = row.original.id;
                        const selectedSource = sources.find((source) => source.id === id);

                        if (selectedSource) {
                            props.onEditClick({
                                id,
                                name: selectedSource.name,
                                channel: null,
                            });
                        }
                    }}
                    renderRowActionMenu={renderRowActionMenu}
                    emptyState={
                        <LegacySetupTableEmptyState text='There aren’t any sources with no channel' />
                    }
                    additionalColumns={React.useMemo(
                        () => [
                            {
                                column: {
                                    id: 'type',
                                    Header: 'Type',
                                    // @ts-expect-error
                                    accessor: (row) => row.type,
                                    dataType: 'component',
                                    width: 150,
                                    Cell: ({cell}: any) => (
                                        <span className='light-text'>{cell.value}</span>
                                    ),
                                },
                                index: 1,
                            },
                        ],
                        []
                    )}
                />
                {hasNextPage ? (
                    <FetchMoreButton buttonText='See all sources' onClick={fetchMore} />
                ) : undefined}
            </LegacySetupTableWrapper>
        </>
    );
}
